import { GridColumnVisibilityModel, GridRowModel, useGridApiRef } from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import useFilters from 'ext-ui/hooks/filters/filters'
import queryString from 'query-string'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DownloadTransactionsModal from 'src/component-modal/DownloadTransactionsModal/DownloadTransactionsModal'
import ManagerReviewRejectionModal from 'src/component-modal/ManagerReviewRejectionModal/ManagerReviewRejectionModal'
import Box from 'src/components/Box/Box'
import {
  DateRangeType,
  getDateRangeTypeFromKey,
  resolveDateRangeTypeWindow,
} from 'src/components/DateFilterDropdown/dateRangeType'
import FullTransactionsTable from 'src/components/FullTransactionsTable/FullTransactionsTable'
import { ApprovalActions } from 'src/components/ReportTransactionsBoxAndLinks/ApprovalActions'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SelectAllTransactionsBox from 'src/components/SelectAllTransactionsBox/SelectAllTransactionsBox'
import { sidenavCollapsedWidth, sidenavExpandedWidth } from 'src/components/SidenavWrapper/sidenav.constants'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import {
  makeBaseTransactionsSchema,
  TransactionsFilters,
  TransactionsFiltersBody,
} from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import TransactionsListFilter from 'src/components/TransactionsListFilter/TransactionsListFilter'
import { CollapseWithFrozenExit } from 'src/components/TransitionWithFrozenExit/TransitionWithFrozenExit'
import { TransactionFilterOptions } from 'src/enums'
import AccountingSummaryBar from 'src/feature/AccountingSummaryBar/AccountingSummaryBar'
import { useEventListener } from 'src/hooks'
import {
  TransactionDateType,
  TransactionListModel,
  TransactionManagerReviewStatus,
  TransactionSort,
} from 'src/models/transaction.models'
import { usePatchTransactionManagerReview } from 'src/queries/expenseReview.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import {
  SavedViewsModel,
  useFetchSavedViews,
} from 'src/queries/savedViews.queries'
import { useQueryTransactions } from 'src/queries/transactions.queries'
import {
  createSetIsSidenavOpen,
  createSetShowSidenavLockedOpen,
} from 'src/redux/actions/app.actions'
import { updateSelectAllTransactionsBoxVisible } from 'src/redux/actions/data/transactions.actions'
import {
  selectIsSidenavLockedOpenFromState,
  selectShowSidenavLargeFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { selectUpdatedReceiptRulesFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseReportIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import {
  selectSelectAllTransactionsBoxVisibleFromState,
  selectSelectedTransactionIdsFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

import styles from './ReportTransactionsBoxAndLinks.style'

export interface TransactionsAnalyticsEventProperties {
  accounts?: string[]
  card_user?: string[]
  clear_date?: boolean
  transaction_date?: boolean
  departments?: string[]
  merchant_category?: string[]
  status?: string[]
  transaction_origin?: string[]
  date_picker?: string
  search?: string
  receipt_status?: string[]
  receipts_required?: boolean
  expense_category_statuses?: string[]
  expense_category_labels?: string[]
  sync_statuses?: boolean
  review_status?: boolean
  amount_range?: boolean
}

interface ReportTransactionsBoxAndLinksProps {
  className?: string
  context: TransactionReportType
  showIntegrationsLink?: boolean
  design: TransactionsTableLargeDesigns
  additionalItems?: React.ReactElement[]
  showConnection?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
  canViewAllTransactions?: boolean
  reportId?: string
  visibleFilters: TransactionFilterOptions[]
  clientHeight: number
  creditCardId?: string
  /* Initial filters to apply on first load, on top of defaults */
  initialFilters?: Partial<TransactionsFilters>
}

const resolveSavedDateRangeFilters = (
  filters: TransactionsFiltersBody,
): TransactionsFiltersBody => {
  const dateRangeType = getDateRangeTypeFromKey(filters.dateRangeType)
  if (!dateRangeType || dateRangeType === DateRangeType.Custom) {
    return filters
  }

  const dateRangeWindow = resolveDateRangeTypeWindow({ dateRangeType })
  if (!dateRangeWindow) return filters

  const since = dateRangeWindow.since.format('YYYY-MM-DD')
  const until = dateRangeWindow.until.format('YYYY-MM-DD')

  if (filters.dateType === TransactionDateType.Clear) {
    return {
      ...filters,
      since: undefined,
      until: undefined,
      sinceClearedAt: since,
      untilClearedAt: until,
    }
  }

  return {
    ...filters,
    since,
    until,
    sinceClearedAt: undefined,
    untilClearedAt: undefined,
  }
}

const resolveSavedVisibleColumns = (
  savedView?: SavedViewsModel,
): string[] | undefined => {
  const columnsFromResponse = savedView?.columns?.visibleColumns
  if (Array.isArray(columnsFromResponse)) return columnsFromResponse

  const columnData = savedView?.columnData
  if (Array.isArray(columnData)) {
    return columnData.filter((field): field is string => typeof field === 'string')
  }
  if (typeof columnData === 'string') {
    try {
      const parsed = JSON.parse(columnData)
      if (Array.isArray(parsed)) {
        return parsed.filter((field): field is string => typeof field === 'string')
      }
    } catch (_error) {
      return undefined
    }
  }

  return undefined
}

const EMPTY_TRANSACTIONS: TransactionListModel[] = []
const EXCLUDED_FILTER_KEYS_FROM_COUNT: (keyof TransactionsFilters)[] = [
  'since',
  'until',
  'dateType',
]

const ReportTransactionsBoxAndLinks: React.FC<
  ReportTransactionsBoxAndLinksProps
> = (props): React.ReactElement => {
  const dataGridHeightOffset = 60
  const transactionsGridApiRef = useGridApiRef()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const queryClient = useQueryClient()
  const updatedReceiptRules = useSelector(selectUpdatedReceiptRulesFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  const showSidenavLarge = useSelector(selectShowSidenavLargeFromState)
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)

  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const user = useSelector(selectUserListModelFromState)
  const { mutate: patchTransactionManagerReview } =
    usePatchTransactionManagerReview()

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false)
  const [showManagerReviewRejectionModal, setShowManagerReviewRejectionModal] =
    useState<boolean>(false)
  const resetColumnsToDefaultRef = useRef<(() => void) | undefined>(undefined)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLDivElement | null>(null)
  const [pendingDefaultColumnsReset, setPendingDefaultColumnsReset] =
    useState<boolean>(false)
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)
  const [tableClientHeight, setTableClientHeight] = useState<number>(
    props.clientHeight,
  )
  const sideNavOffset =
    showSidenavLarge
      ? sideNavLockedOpen
        ? sidenavExpandedWidth
        : sidenavCollapsedWidth
      : 0

  const updateTableHeight = useCallback((): void => {
    if (!tableRef.current) return

    const tableTop = tableRef.current.getBoundingClientRect().top
    let boundaryBottom = window.innerHeight

    if (!isFullScreen) {
      const layoutContainer = tableRef.current.closest(
        '.LayoutShell, .LayoutPage',
      ) as HTMLElement | null
      if (layoutContainer) {
        const layoutPaddingBottom =
          parseFloat(window.getComputedStyle(layoutContainer).paddingBottom) || 0
        boundaryBottom = Math.min(
          boundaryBottom,
          window.innerHeight - layoutPaddingBottom,
        )
      }
    }

    const availableHeight = Math.max(0, boundaryBottom - tableTop)
    const measuredClientHeight = Math.floor(
      availableHeight + dataGridHeightOffset,
    )
    const nextHeight = isFullScreen
      ? measuredClientHeight
      : props.context === TransactionReportType.AccountingTransactions
        ? props.clientHeight
        : Math.min(props.clientHeight, measuredClientHeight)

    setTableClientHeight((prevState) =>
      prevState === nextHeight ? prevState : nextHeight,
    )
  }, [dataGridHeightOffset, isFullScreen, props.clientHeight, props.context])

  useEventListener('resize', updateTableHeight)
  
  const [hasUnsavedColumnChanges, setHasUnsavedColumnChanges] =
    useState<boolean>(false)

  // pick a safe initial value (auth)
  const [sort, setSort] = useState<TransactionSort>(
    TransactionSort.dateAuthDesc,
  )

  // expense categories schema needed to pull out labels for api request
  const { data: expenseCategories } = useQueryExpenseCategories({
    filters: { active: true },
  })

  const filterOptions = useMemo(
    () =>
      makeBaseTransactionsSchema({
        activeConnectedPlatform: activeIntegration?.connectedPlatform,
        reportId,
        context: props.context,
        userId: user?.id,
        creditCardId: props.creditCardId,
        expenseCategories,
        sort,
      }),
    [
      activeIntegration?.connectedPlatform,
      reportId,
      props.context,
      props.creditCardId,
      expenseCategories,
      sort,
    ],
  )

  const { data: savedViewsData } = useFetchSavedViews(props.context)
  const savedFiltersMap = useMemo(() => {
    return Object.fromEntries(
      (savedViewsData?.userSavedViews || []).map((view) => [
        view.id,
        filterOptions.normalizeQueryParams(
          resolveSavedDateRangeFilters(
            view.filters as TransactionsFiltersBody,
          ) as unknown as queryString.ParsedQuery<string>,
        ),
      ]),
    ) as Record<string, Partial<TransactionsFilters>>
  }, [savedViewsData?.userSavedViews, filterOptions])
  const filtersHookOptions = useMemo(
    () => ({
      ...filterOptions,
      initialFilters: props.initialFilters,
      savedFiltersMap,
      excludedFilterKeysFromCount: EXCLUDED_FILTER_KEYS_FROM_COUNT,
    }),
    [filterOptions, props.initialFilters, savedFiltersMap],
  )

  const {
    filters: currFilters,
    apiQueryParams,
    handleFilterChange,
    resetFilters,
    resetSingleFilter,
    hasViewFiltersChange,
    selectedFiltersCount,
  } = useFilters<TransactionsFilters, TransactionsFiltersBody>(filtersHookOptions)
  const apiQueryParamsKey = useMemo(
    () => queryString.stringify(apiQueryParams),
    [apiQueryParams],
  )
  const stableApiQueryParams = useMemo(
    () => apiQueryParams,
    [apiQueryParamsKey],
  )

  // One-time snapshot of the very first filters (initial URL + defaults)
  const initialFilters = React.useRef(currFilters).current

  // once filters resolve (from URL or defaults), sync sort to dateType
  useEffect(() => {
    const dt = currFilters.dateType ?? TransactionDateType.Transaction
    setSort(
      dt === TransactionDateType.Clear
        ? TransactionSort.dateClearDesc
        : TransactionSort.dateAuthDesc,
    )
  }, [currFilters.dateType])

  // fire analytics when filters are applied (use debounced value)
  useEffect(() => {
    const filterApplied = Object.keys(stableApiQueryParams).length > 1
    if (filterApplied) {
      analytics?.track(
        props.context.toLowerCase() + ' transactions filter applied',
        {
          search: !!stableApiQueryParams.search,
          date_picker: stableApiQueryParams.dateType,
          clear_date: stableApiQueryParams.dateType === TransactionDateType.Clear,
          transaction_date:
            stableApiQueryParams.dateType === TransactionDateType.Transaction,
          amount_range:
            stableApiQueryParams?.maxClearingBillingCents !== undefined ||
            stableApiQueryParams?.minClearingBillingCents !== undefined,
          status: !!stableApiQueryParams.status?.length,
          transaction_origin: !!stableApiQueryParams.source?.length,
          expense_category_status:
            !!stableApiQueryParams.expenseCategoryStatuses?.length,
          expense_category_labels:
            !!stableApiQueryParams.expenseCategoryLabel?.length,
          receipt_status: !!stableApiQueryParams.receiptStatus?.length,
          receipts_required: !!stableApiQueryParams.receiptRequired,
          sync_status: !!stableApiQueryParams.syncStatuses?.length,
          card_user: !!stableApiQueryParams.recipientOrPhysicalCardUserId?.length,
          departments: !!stableApiQueryParams.departments?.length,
          accounts: !!stableApiQueryParams.creditCardId?.length,
          merchant_category: !!stableApiQueryParams.mccGroup?.length,
          review_status: !!stableApiQueryParams.reviewStatus?.length,
        },
      )
    }
  }, [props.context, stableApiQueryParams])

  const { data, fetchNextPage, isFetching, hasNextPage, complete } =
    useQueryTransactions({
      filters: stableApiQueryParams,
      myTransactions: props.context === TransactionReportType.MyTransactions,
    })
  const transactions = useMemo<TransactionListModel[]>(
    () => data?.transactions ?? EMPTY_TRANSACTIONS,
    [data?.transactions],
  )

  const selectAllTransactionsBoxVisible = useSelector(
    selectSelectAllTransactionsBoxVisibleFromState,
  )
  const shouldShowSelectAllTransactionsBox = useMemo(
    () =>
      selectedTransactionIds.size === transactions.length &&
      selectedTransactionIds.size > 0 &&
      !complete,
    [complete, selectedTransactionIds.size, transactions.length],
  )

  const transactionInfiniteScrollProps: InfiniteScrollProps<TransactionsFiltersBody> =
    useMemo(
      () => ({
        fetch: () => fetchNextPage(),
        loading: isFetching,
        complete,
        hasNextPage,
      }),
      [complete, fetchNextPage, hasNextPage, isFetching],
    )

  // Reset transactions table and filter when receipt rules is updated
  useEffect((): void => {
    if (updatedReceiptRules) {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
    }
  }, [updatedReceiptRules])

  const defaultColumnVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
    const defaults: GridColumnVisibilityModel = {
      date: true,
      merchant: true,
      card: true,
      cardUser: true,
      cardholderEmail: false,
      expenseCategories: true,
      status: false,
      amount: true,
      currency: false,
      authBillingAmountCents: false,
      authBillingCurrency: false,
      authMerchantAmountCents: false,
      authMerchantCurrency: false,
      authExchangeRate: false,
      attachmentsCount: false,
      creditCardDisplayName: false,
      creditCardType: false,
      receipts: true,
      originalMerchant: false,
      managerReviewStatus: false,
      finalReview: false,
    }

    switch (props.context) {
      case TransactionReportType.MyTransactions:
        defaults.cardUser = false
        break
      case TransactionReportType.NeedsApproval:
        defaults.status = true
        break
      case TransactionReportType.AccountingTransactions:
        defaults.managerReviewStatus = true
        defaults.finalReview = true
        break
    }

    return defaults
  }, [props.context])

  // select all box cleanup
  useEffect(() => {
    if (shouldShowSelectAllTransactionsBox === selectAllTransactionsBoxVisible) {
      return
    }

    dispatch(
      updateSelectAllTransactionsBoxVisible(shouldShowSelectAllTransactionsBox),
    )
  }, [
    dispatch,
    selectAllTransactionsBoxVisible,
    shouldShowSelectAllTransactionsBox,
  ])

  const [rejectedTxnIds, setRejectedTxnIds] = useState<string[]>([])
  const onReview = useCallback(
    (txId: string, status: TransactionManagerReviewStatus) => {
      if (status === TransactionManagerReviewStatus.Rejected) {
        setShowManagerReviewRejectionModal(true)
        setRejectedTxnIds([txId])
      } else {
        patchTransactionManagerReview({
          body: {
            transactionIds: [txId],
            managerReviewStatus: status,
          },
          filters: stableApiQueryParams,
        })
        analytics?.track('manager review actioned txn', {
          action: 'approved',
        })
      }
    },
    [analytics, patchTransactionManagerReview, stableApiQueryParams],
  )

  const rowApprovalActions = useCallback(
    (params: GridRowModel) => {
      return (
        <ApprovalActions txId={params.row.transaction.id} onReview={onReview} />
      )
    },
    [onReview],
  )

  const handleOpenDownloadModal = useCallback((): void => {
    setShowDownloadModal(true)
  }, [])
  const handleCloseManagerReviewRejectionModal = useCallback((): void => {
    setShowManagerReviewRejectionModal(false)
  }, [])

  const getCurrentColumnData = useCallback((): string | undefined => {
    const visibleColumns = transactionsGridApiRef.current
      ?.getVisibleColumns()
      .map((column) => column.field)
      .filter(
        (field) =>
          field !== '__check__' && field !== 'chevron' && field !== 'hover-actions',
      )

    if (!visibleColumns) return undefined
    return JSON.stringify(visibleColumns)
  }, [transactionsGridApiRef])

  const selectedSavedViewVisibleColumns = useMemo(() => {
    const selectedViewId = currFilters.viewId
    if (!selectedViewId) return undefined

    const selectedView = savedViewsData?.userSavedViews?.find(
      (view) => view.id === selectedViewId,
    )
    return resolveSavedVisibleColumns(selectedView)
  }, [currFilters.viewId, savedViewsData?.userSavedViews])

  const savedViews = useMemo(
    () => savedViewsData?.userSavedViews || [],
    [savedViewsData?.userSavedViews],
  )

  useEffect(() => {
    if (!currFilters.viewId) return
    if (selectedSavedViewVisibleColumns !== undefined) return

    // Legacy or malformed saved views may not have column data.
    // In that case, selecting the view should still restore a deterministic layout.
    resetColumnsToDefaultRef.current?.()
  }, [currFilters.viewId, selectedSavedViewVisibleColumns])

  const hasViewChange = hasViewFiltersChange || hasUnsavedColumnChanges

  useEffect(() => {
    if (currFilters.viewId !== undefined) {
      setHasUnsavedColumnChanges(false)
    }
  }, [currFilters.viewId])

  const handleResetToDefaultView = useCallback((): void => {
    setHasUnsavedColumnChanges(false)
    setPendingDefaultColumnsReset(true)
    resetFilters()
  }, [resetFilters])

  const handleUserChangedColumns = useCallback((): void => {
    setHasUnsavedColumnChanges(true)
    if (currFilters.viewId !== undefined) {
      handleFilterChange({})
    }
  }, [currFilters.viewId, handleFilterChange])

  const handleFullScreenToggle = useCallback((): void => {
    const nextIsFullScreen = !isFullScreen
    if (nextIsFullScreen) {
      dispatch(createSetShowSidenavLockedOpen(false))
      dispatch(createSetIsSidenavOpen(false))
    } else {
      dispatch(createSetShowSidenavLockedOpen(true))
      dispatch(createSetIsSidenavOpen(true))
    }
    setIsFullScreen(nextIsFullScreen)
  }, [dispatch, isFullScreen])

  const handleRegisterResetColumnsToDefault = useCallback(
    (reset: () => void): void => {
      resetColumnsToDefaultRef.current = reset
    },
    [],
  )

  useEffect(() => {
    if (!pendingDefaultColumnsReset) return
    if (currFilters.viewId !== undefined) return

    resetColumnsToDefaultRef.current?.()
    setPendingDefaultColumnsReset(false)
  }, [pendingDefaultColumnsReset, currFilters.viewId])

  useLayoutEffect(() => {
    updateTableHeight()
    const animationFrame = window.requestAnimationFrame(updateTableHeight)
    return () => window.cancelAnimationFrame(animationFrame)
  }, [updateTableHeight, selectAllTransactionsBoxVisible])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    if (!boxRef.current && !tableRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight()
    })
    if (boxRef.current) resizeObserver.observe(boxRef.current)
    if (tableRef.current) resizeObserver.observe(tableRef.current)

    return () => resizeObserver.disconnect()
  }, [updateTableHeight])

  return (
    <>
      {props.context === TransactionReportType.AccountingTransactions && (
        // this is defined here to give it access to the filters
        <AccountingSummaryBar
          filters={currFilters}
          handleFilterChange={handleFilterChange}
          resetSingleFilter={resetSingleFilter}
          apiFiltersBody={stableApiQueryParams}
        />
      )}
      <Box
        refObj={boxRef}
        className={cn('ReportTransactionsBoxAndLinks', props.className, {
          fullScreen: isFullScreen,
        })}
        css={css`
          ${styles}
          ${isFullScreen &&
          css`
            &.ReportTransactionsBoxAndLinks.fullScreen {
              margin-top: 0 !important;
              top: ${fixedElementPushdown}px;
              left: ${sideNavOffset}px;
              right: 0;
              bottom: 0;
              width: auto;
              height: auto;
            }
          `}
        `}
        noPadding
        noShadow
        outlineBorder
      >
        <CollapseWithFrozenExit
          in={
            props.context !== TransactionReportType.NeedsApproval &&
            selectAllTransactionsBoxVisible
          }
          key={'showSelectAllBox'}
          mountOnEnter
          unmountOnExit
        >
          <SelectAllTransactionsBox
            filtersBody={stableApiQueryParams}
            selectedTransactionCount={selectedTransactionIds.size}
          />
        </CollapseWithFrozenExit>
        <TransactionsListFilter
          initialFilterValues={initialFilters}
          defaultFilters={filterOptions.defaultFilters}
          filterValues={currFilters}
          hasViewChange={hasViewChange}
          selectedFiltersCount={selectedFiltersCount}
          handleFilterChange={handleFilterChange}
          resetFilters={resetFilters}
          handleResetToDefaultView={handleResetToDefaultView}
          resetSingleFilter={resetSingleFilter}
          apiQueryParams={stableApiQueryParams}
          reportId={props.reportId}
          context={props.context}
          visibleFilters={props.visibleFilters}
          isTransactionsFetching={isFetching}
          handleDownloadClick={handleOpenDownloadModal}
          handleFullScreenClick={handleFullScreenToggle}
          isFullScreen={isFullScreen}
          savedViews={savedViews}
          getCurrentColumnData={getCurrentColumnData}
        />
        <div className="ReportTransactionsBoxAndLinks table" ref={tableRef}>
          <FullTransactionsTable
            apiRef={transactionsGridApiRef}
            savedVisibleColumns={selectedSavedViewVisibleColumns}
            onRegisterResetColumnsToDefault={
              handleRegisterResetColumnsToDefault
            }
            onUserChangedColumns={handleUserChangedColumns}
            transactions={transactions}
            fullyLoadedTransactions={complete}
            emptyStateDesign={
              props.context === TransactionReportType.NeedsApproval
                ? TableEmptyStateDesigns.ExpensesNeedsApproval
                : TableEmptyStateDesigns.AccountTransactions
            }
            design={props.design}
            infiniteScrollProps={transactionInfiniteScrollProps}
            filters={stableApiQueryParams}
            reportId={props.reportId}
            clientHeight={tableClientHeight}
            setSort={setSort}
            defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            context={props.context}
            rowApprovalActions={
              props.context === TransactionReportType.NeedsApproval
                ? rowApprovalActions
                : undefined
            }
          />
        </div>
        {showDownloadModal ? (
          <DownloadTransactionsModal
            isOpen={showDownloadModal}
            onClose={() => {
              setShowDownloadModal(false)
            }}
            apiQueryParams={stableApiQueryParams}
          />
        ) : null}
        {showManagerReviewRejectionModal ? (
          <ManagerReviewRejectionModal
            isOpen={showManagerReviewRejectionModal}
            onClose={handleCloseManagerReviewRejectionModal}
            transactionIds={rejectedTxnIds}
          />
        ) : null}
      </Box>
    </>
  )
}

export default ReportTransactionsBoxAndLinks
